import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import CreateTicketModal from '../../components/admin/CreateTicketModal';
import DocumentActionModal from '../../components/admin/DocumentActionModal';
import { apiService } from '../../services/api';
import { renderCountryFlag } from '../../utils/flagUtils';
import { formatDateTime } from '../../utils/timezoneUtils';

interface RequestData {
  id: number;
  request_number: string;
  status: string;
  progress_percentage: number;
  company_name: string;
  business_type: string;
  created_at: string;
  updated_at: string;
  user: {
    name: string;
    email: string;
    whatsapp: string;
  };
  package: {
    name: string;
    price: number;
    currency: string;
  };
  country: {
    name: string;
    code: string;
    flag: string;
  };
  payment: {
    status: string;
    amount: number;
    invoice_number: string;
  };
  documents: {
    total: number;
    pending: number;
  };
  additional_info: any;
  status_history: any[];
}

interface DetailedRequest {
  id: number;
  request_number: string;
  status: string;
  progress_percentage: number;
  company_name: string;
  business_type: string;
  created_at: string;
  updated_at: string;
  user: {
    id: number;
    name: string;
    email: string;
    whatsapp: string;
    registered: string;
  };
  package: {
    id: number;
    name: string;
    price: number;
    currency: string;
    features: string[];
  };
  country: {
    name: string;
    code: string;
    flag: string;
  };
  payment: {
    status: string;
    amount: number;
    invoice_number: string;
  };
  additional_info: any;
  dynamic_fields?: {
    key: string;
    label: string;
    label_ar?: string;
    type: string;
    value: any;
    display_value: string;
    is_required: boolean;
    sort_order: number;
  }[];
  partners: {
    partner_name: string;
    partner_address: string;
    ownership_percentage: number;
  }[];
  documents: {
    id: number;
    document_type: string;
    original_filename: string;
    file_size: number;
    mime_type: string;
    status: string;
    admin_comment: string;
    uploaded_at: string;
    reviewed_at: string;
  }[];
  status_history: any[];
  payments: any[];
}

const RequestManagement: React.FC = () => {
  const [requests, setRequests] = useState<RequestData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<DetailedRequest | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState({
    request_id: 0,
    status: '',
    comment: '',
    progress_percentage: 0
  });

  const [statusUpdateFiles, setStatusUpdateFiles] = useState<File[]>([]);

  const [showCommunicationModal, setShowCommunicationModal] = useState(false);
  const [communication, setCommunication] = useState({
    request_id: 0,
    subject: '',
    message: ''
  });

  const [downloadingDocs, setDownloadingDocs] = useState<Set<number>>(new Set());
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const [selectedRequestForTicket, setSelectedRequestForTicket] = useState<DetailedRequest | RequestData | null>(null);
  
  const [showDocumentActionModal, setShowDocumentActionModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [documentAction, setDocumentAction] = useState<'approve' | 'reject'>('approve');

  const statusOptions = [
    { value: 'submitted', label: 'Submitted', color: 'blue' },
    { value: 'processing', label: 'Processing', color: 'yellow' },
    { value: 'in_review', label: 'In Review', color: 'orange' },
    { value: 'documents_requested', label: 'Documents Requested', color: 'purple' },
    { value: 'documents_received', label: 'Documents Received', color: 'indigo' },
    { value: 'under_review', label: 'Under Review', color: 'orange' },
    { value: 'approved', label: 'Approved', color: 'green' },
    { value: 'completed', label: 'Completed', color: 'green' },
    { value: 'rejected', label: 'Rejected', color: 'red' },
    { value: 'cancelled', label: 'Cancelled', color: 'gray' }
  ];

  useEffect(() => {
    fetchRequests();
  }, [statusFilter, currentPage]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(statusFilter !== 'all' && { status: statusFilter })
      });

      const response = await fetch(`/backend/api/admin/requests/?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (result.success) {
        setRequests(result.requests || []);
        setTotalPages(result.pagination?.total_pages || 1);
      } else {      }
    } catch (error) {    } finally {
      setLoading(false);
    }
  };

  const fetchRequestDetails = async (requestId: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/backend/api/admin/requests/?id=${requestId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (result.success) {
        // Process status history to include admin attachments
        const processedRequest = { ...result.request };
        if (processedRequest.status_history && processedRequest.documents) {
          processedRequest.status_history = processedRequest.status_history.map((history: any) => {
            // Find admin attachments that were uploaded around the same time as this status update
            const historyDate = new Date(history.created_at);
            const attachments = processedRequest.documents.filter((doc: any) => {
              if (doc.uploaded_by !== 'admin' || doc.document_type !== 'admin_status_update') return false;
              const docDate = new Date(doc.uploaded_at);
              // Consider attachments uploaded within 5 minutes of the status update as related
              const timeDiff = Math.abs(historyDate.getTime() - docDate.getTime());
              return timeDiff <= 5 * 60 * 1000; // 5 minutes in milliseconds
            });
            
            return {
              ...history,
              attachments: attachments.length > 0 ? attachments : undefined
            };
          });
        }
        setSelectedRequest(processedRequest);
        setShowDetails(true);
      } else {      }
    } catch (error) {    }
  };

  const updateRequestStatus = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      // If there are files to upload, use FormData
      if (statusUpdateFiles.length > 0) {
        const formData = new FormData();
        formData.append('request_id', statusUpdate.request_id.toString());
        formData.append('status', statusUpdate.status);
        formData.append('comment', statusUpdate.comment);
        formData.append('progress_percentage', statusUpdate.progress_percentage.toString());
        
        // Add files to FormData
        statusUpdateFiles.forEach((file, index) => {
          formData.append(`files[${index}]`, file);
        });

        const response = await fetch('/backend/api/admin/requests/?action=update_status_with_files', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });

        const result = await response.json();
        
        if (result.success) {
          setShowStatusModal(false);
          setStatusUpdateFiles([]);
          fetchRequests();
          if (selectedRequest && selectedRequest.id === statusUpdate.request_id) {
            fetchRequestDetails(selectedRequest.id);
          }
        } else {
          alert('Failed to update status: ' + result.error);
        }
      } else {
        // Regular JSON update without files
        const response = await fetch('/backend/api/admin/requests/?action=update_status', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(statusUpdate),
        });

        const result = await response.json();
        
        if (result.success) {
          setShowStatusModal(false);
          fetchRequests();
          if (selectedRequest && selectedRequest.id === statusUpdate.request_id) {
            fetchRequestDetails(selectedRequest.id);
          }
        } else {
          alert('Failed to update status: ' + result.error);
        }
      }
    } catch (error) {      alert('Failed to update status. Please try again.');
    }
  };

  const sendCommunication = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/backend/api/admin/requests/?action=communicate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(communication),
      });

      const result = await response.json();
      
      if (result.success) {
        setShowCommunicationModal(false);
        setCommunication({ request_id: 0, subject: '', message: '' });
        alert('Communication sent successfully');
      } else {
        alert('Failed to send communication: ' + result.error);
      }
    } catch (error) {      alert('Failed to send communication. Please try again.');
    }
  };

  const getStatusColorClasses = (status: string) => {
    const statusOption = statusOptions.find(s => s.value === status);
    const color = statusOption?.color || 'gray';
    
    const classes: Record<string, string> = {
      gray: 'bg-gray-100 text-gray-800',
      blue: 'bg-blue-100 text-blue-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      orange: 'bg-orange-100 text-orange-800',
      purple: 'bg-purple-100 text-purple-800',
      indigo: 'bg-indigo-100 text-indigo-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800'
    };
    
    return classes[color] || 'bg-gray-100 text-gray-800';
  };

  const formatDateDisplay = (dateString: string) => {
    return formatDateTime(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }, 'en-US');
  };

  // Helper function to format status comment (handle JSON rejection data)
  const formatStatusComment = (comment: string | null) => {
    if (!comment) return null;

    try {
      const data = JSON.parse(comment);

      // Check if it's a document rejection structure
      if (data.type === 'document_rejection') {
        const formattedMessage = `Document Rejected: '${data.filename}' (${data.document_type})\n\nRejection Reason: ${data.rejection_reason}\n\nPlease upload a corrected version of this document.`;
        return formattedMessage;
      }

      // If it's JSON but not recognized format, return as-is
      return comment;
    } catch (e) {
      // Not JSON, return as plain text
      return comment;
    }
  };

  const handleStatusUpdate = (request: DetailedRequest | RequestData) => {
    const currentProgress = request.progress_percentage || 0;
    setStatusUpdate({
      request_id: request.id,
      status: request.status,
      comment: '',
      progress_percentage: currentProgress
    });
    setStatusUpdateFiles([]);
    setShowStatusModal(true);
  };

  const handleFileRemove = (index: number) => {
    setStatusUpdateFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileAdd = (files: FileList) => {
    const newFiles = Array.from(files).filter(file => {
      // Validate file type and size
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      if (!allowedTypes.includes(file.type)) {
        alert(`File ${file.name} is not allowed. Please upload PDF, Word, or Image files only.`);
        return false;
      }
      
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      
      return true;
    });
    
    setStatusUpdateFiles(prev => [...prev, ...newFiles]);
  };

  const handleCommunicate = (request: DetailedRequest | RequestData) => {
    setCommunication({
      request_id: request.id,
      subject: `Regarding your request ${request.request_number || `#${request.id}`}`,
      message: ''
    });
    setShowCommunicationModal(true);
  };

  const downloadDocument = async (documentId: number, filename: string) => {
    try {
      setDownloadingDocs(prev => {
        const newSet = new Set(prev);
        newSet.add(documentId);
        return newSet;
      });
      
      const blob = await apiService.downloadDocument(documentId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
    } catch (error) {      alert('Failed to download document: ' + (error as Error).message);
    } finally {
      setDownloadingDocs(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  const handleCreateSupportTicket = (request: DetailedRequest | RequestData) => {
    setSelectedRequestForTicket(request);
    setShowCreateTicketModal(true);
  };

  const handleTicketCreated = (ticketId: number) => {
    // Show success message
    alert(`Support ticket created successfully! Ticket ID: ${ticketId}`);
    
    // Optionally redirect to the ticket
    // navigate(`/admin/tickets/${ticketId}`);
    
    // Close modal and reset state
    setShowCreateTicketModal(false);
    setSelectedRequestForTicket(null);
  };

  const handleDocumentApprove = (document: any) => {
    setSelectedDocument(document);
    setDocumentAction('approve');
    setShowDocumentActionModal(true);
  };

  const handleDocumentReject = (document: any) => {
    setSelectedDocument(document);
    setDocumentAction('reject');
    setShowDocumentActionModal(true);
  };

  const handleDocumentActionSuccess = () => {
    // Refresh the request details to show updated document status
    if (selectedRequest) {
      fetchRequestDetails(selectedRequest.id);
    }
    // Close modal and reset state
    setShowDocumentActionModal(false);
    setSelectedDocument(null);
  };

  const generateProgressOptions = (currentProgress: number) => {
    const options = [];
    for (let i = currentProgress; i <= 100; i += 10) {
      options.push({ value: i, label: `${i}%` });
    }
    return options;
  };

  if (loading && requests.length === 0) {
    return (
      <AdminLayout>
        <LoadingSpinner />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Request Management</h1>
          
          {/* Filters */}
          <div className="flex items-center space-x-4">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="all">All Statuses</option>
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Requests Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Request
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Package
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {requests.map((request) => (
                <tr key={request.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="mr-3">
                        {renderCountryFlag(
                          request.country.code,
                          request.country.flag,
                          request.country.name,
                          'w-10 h-8 object-cover rounded-sm'
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {request.request_number}
                        </div>
                        <div className="text-sm text-gray-500">
                          {request.company_name}
                        </div>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {request.user.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {request.user.email}
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {request.package.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      ${request.package.price} {request.package.currency}
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColorClasses(request.status)}`}>
                        {statusOptions.find(s => s.value === request.status)?.label || request.status}
                      </span>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-blue-600 h-1.5 rounded-full"
                          style={{ width: `${request.progress_percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-500">{request.progress_percentage}%</span>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      request.payment.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {request.payment.status === 'paid' ? 'Paid' : 'Pending'}
                    </span>
                    {request.documents.pending > 0 && (
                      <div className="text-xs text-orange-600 mt-1">
                        {request.documents.pending} docs pending
                      </div>
                    )}
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateDisplay(request.created_at)}
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => fetchRequestDetails(request.id)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(request)}
                      className="text-green-600 hover:text-green-900"
                    >
                      Update
                    </button>
                    <button
                      onClick={() => handleCommunicate(request)}
                      className="text-purple-600 hover:text-purple-900"
                    >
                      Message
                    </button>
                    <button
                      onClick={() => handleCreateSupportTicket(request)}
                      className="text-orange-600 hover:text-orange-900"
                      title="Create Support Ticket"
                    >
                      Support
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Request Details Modal */}
        {showDetails && selectedRequest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
                <h2 className="text-xl font-semibold">
                  {selectedRequest.request_number} - {selectedRequest.company_name}
                </h2>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Client Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium mb-3">Client Information</h3>
                  <div className="space-y-2 text-sm">
                    <div><strong>Name:</strong> {selectedRequest.user.name}</div>
                    <div><strong>Email:</strong> {selectedRequest.user.email}</div>
                    <div><strong>WhatsApp:</strong> {selectedRequest.user.whatsapp}</div>
                    <div><strong>Registered:</strong> {formatDateDisplay(selectedRequest.user.registered)}</div>
                  </div>
                </div>

                {/* Request Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium mb-3">Request Details</h3>
                  <div className="space-y-2 text-sm">
                    <div><strong>Package:</strong> {selectedRequest.package.name}</div>
                    <div><strong>Country:</strong> {selectedRequest.country.name}</div>
                    <div><strong>Price:</strong> ${selectedRequest.package.price} {selectedRequest.package.currency}</div>
                    <div><strong>Created:</strong> {formatDateDisplay(selectedRequest.created_at)}</div>
                    <div><strong>Updated:</strong> {formatDateDisplay(selectedRequest.updated_at)}</div>
                  </div>
                </div>

                {/* Status & Progress */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium mb-3">Status & Progress</h3>
                  <div className="space-y-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColorClasses(selectedRequest.status)}`}>
                      {statusOptions.find(s => s.value === selectedRequest.status)?.label || selectedRequest.status}
                    </span>
                    <div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-blue-600 h-3 rounded-full"
                          style={{ width: `${selectedRequest.progress_percentage}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{selectedRequest.progress_percentage}% Complete</p>
                    </div>
                  </div>
                </div>

                {/* Dynamic Fields */}
                {selectedRequest.dynamic_fields && selectedRequest.dynamic_fields.length > 0 && (
                  <div className="lg:col-span-3 bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium mb-3">Request Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {selectedRequest.dynamic_fields.map((field, index) => (
                        <div key={index} className="space-y-1">
                          <div>
                            <strong>{field.label}:</strong> {field.display_value}
                            {field.is_required && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documents */}
                {selectedRequest.documents.length > 0 && (
                  <div className="lg:col-span-3 bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium mb-3">Documents</h3>
                    <div className="grid gap-3">
                      {selectedRequest.documents.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-white rounded border">
                          <div className="flex-1">
                            <p className="font-medium">{doc.original_filename}</p>
                            <p className="text-sm text-gray-600">
                              {doc.document_type} • {(doc.file_size / 1024).toFixed(1)} KB • {formatDateDisplay(doc.uploaded_at)}
                            </p>
                            {doc.admin_comment && (
                              <p className="text-sm text-gray-700 mt-1">
                                <strong>Admin Note:</strong> {doc.admin_comment}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              doc.status === 'approved' ? 'bg-green-100 text-green-800' :
                              doc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {doc.status}
                            </span>
                            
                            {/* Document Action Buttons */}
                            {doc.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleDocumentApprove(doc)}
                                  className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                                  title="Approve document"
                                >
                                  ✓ Approve
                                </button>
                                <button
                                  onClick={() => handleDocumentReject(doc)}
                                  className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                                  title="Reject document"
                                >
                                  ✗ Reject
                                </button>
                              </>
                            )}
                            
                            {/* Download Button */}
                            <button
                              onClick={() => downloadDocument(doc.id, doc.original_filename)}
                              disabled={downloadingDocs.has(doc.id)}
                              className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Download document"
                            >
                              {downloadingDocs.has(doc.id) ? (
                                <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                              ) : (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status History Section */}
                <div className="lg:col-span-3 bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium mb-3">Status History</h3>
                  {selectedRequest.status_history && selectedRequest.status_history.length > 0 ? (
                    <div className="relative">
                      {/* Timeline line */}
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-300"></div>
                      
                      <div className="space-y-6">
                        {selectedRequest.status_history.map((history: any, index: number) => (
                          <div key={history.id} className="relative flex items-start space-x-4">
                            {/* Timeline dot */}
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                              index === 0 ? 'bg-blue-600' : 'bg-gray-400'
                            }`}>
                              <div className="w-2 h-2 bg-white rounded-full"></div>
                            </div>
                            
                            <div className="flex-1 bg-white border rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium text-gray-900">
                                  {statusOptions.find(s => s.value === history.new_status)?.label || history.new_status}
                                </h4>
                                <div className="flex items-center space-x-2 text-sm text-gray-500">
                                  <span>{formatDateDisplay(history.created_at)}</span>
                                  {history.changed_by_name && (
                                    <span>• by {history.changed_by_name}</span>
                                  )}
                                </div>
                              </div>
                              
                              {history.comment && (
                                <p className="text-gray-700 mb-2 whitespace-pre-line">{formatStatusComment(history.comment)}</p>
                              )}
                              
                              
                              {history.progress_percentage !== null && (
                                <p className="text-sm text-gray-600">
                                  Progress updated to: <span className="font-medium">{history.progress_percentage}%</span>
                                </p>
                              )}
                              
                              {/* Admin Attachments */}
                              {history.attachments && history.attachments.length > 0 && (
                                <div className="mt-3 p-3 bg-blue-50 border-l-4 border-blue-400 rounded">
                                  <h5 className="text-sm font-medium text-blue-900 mb-2">
                                    📎 Files attached by admin:
                                  </h5>
                                  <div className="space-y-2">
                                    {history.attachments.map((attachment: any) => (
                                      <div key={attachment.id} className="flex items-center justify-between p-2 bg-white rounded border border-blue-200">
                                        <div className="flex items-center space-x-2">
                                          <span className="text-lg">
                                            {attachment.mime_type?.includes('pdf') ? '📄' : 
                                             attachment.mime_type?.includes('image') ? '🖼️' : 
                                             attachment.mime_type?.includes('word') || attachment.mime_type?.includes('document') ? '📝' : '📎'}
                                          </span>
                                          <div>
                                            <p className="text-sm font-medium text-gray-900">{attachment.original_filename}</p>
                                            <p className="text-xs text-gray-500">
                                              {(attachment.file_size / 1024).toFixed(1)} KB • Uploaded {formatDateDisplay(attachment.uploaded_at)}
                                            </p>
                                          </div>
                                        </div>
                                        <button
                                          onClick={() => downloadDocument(attachment.id, attachment.original_filename)}
                                          disabled={downloadingDocs.has(attachment.id)}
                                          className="text-blue-600 hover:text-blue-800 text-sm font-medium disabled:opacity-50 flex items-center space-x-1"
                                          title="Download file"
                                        >
                                          {downloadingDocs.has(attachment.id) ? (
                                            <>
                                              <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                              <span className="text-xs">Downloading...</span>
                                            </>
                                          ) : (
                                            <>
                                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                              </svg>
                                              <span className="text-xs">Download</span>
                                            </>
                                          )}
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <div className="text-4xl mb-4">📋</div>
                      <p>No status history available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end space-x-3">
                <button
                  onClick={() => handleStatusUpdate(selectedRequest)}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Update Status
                </button>
                <button
                  onClick={() => handleCommunicate(selectedRequest)}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  Send Message
                </button>
                <button
                  onClick={() => handleCreateSupportTicket(selectedRequest)}
                  className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                >
                  Create Support Ticket
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status Update Modal */}
        {showStatusModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">Update Request Status</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={statusUpdate.status}
                    onChange={(e) => setStatusUpdate(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Progress Percentage (can only increase in 10% steps)
                  </label>
                  <select
                    value={statusUpdate.progress_percentage}
                    onChange={(e) => setStatusUpdate(prev => ({ ...prev, progress_percentage: parseInt(e.target.value) }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    {generateProgressOptions(statusUpdate.progress_percentage || 0).map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Current: {statusUpdate.progress_percentage}% • Progress can only be increased
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Comment
                  </label>
                  <textarea
                    value={statusUpdate.comment}
                    onChange={(e) => setStatusUpdate(prev => ({ ...prev, comment: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    rows={3}
                    placeholder="Add a comment about this status update..."
                  />
                </div>

                {/* File Attachment Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attach Files (Optional)
                  </label>
                  
                  {/* File Upload Area */}
                  <div className="border-2 border-dashed border-gray-300 rounded-md p-4 hover:border-gray-400 transition-colors">
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                      onChange={(e) => e.target.files && handleFileAdd(e.target.files)}
                      className="hidden"
                      id="status-file-upload"
                    />
                    <label htmlFor="status-file-upload" className="cursor-pointer">
                      <div className="text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="mt-2">
                          <p className="text-sm text-gray-600">
                            <span className="font-medium text-blue-600 hover:text-blue-500">Click to upload files</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-500">PDF, Word, or Images up to 10MB each</p>
                        </div>
                      </div>
                    </label>
                  </div>
                  
                  {/* Selected Files Display */}
                  {statusUpdateFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-sm font-medium text-gray-700">Selected Files:</p>
                      {statusUpdateFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                          <div className="flex items-center space-x-2">
                            <span className="text-lg">
                              {file.type.includes('pdf') ? '📄' : 
                               file.type.includes('image') ? '🖼️' : 
                               file.type.includes('word') || file.type.includes('document') ? '📝' : '📎'}
                            </span>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{file.name}</p>
                              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleFileRemove(index)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remove file"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setStatusUpdateFiles([]);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={updateRequestStatus}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Update Status {statusUpdateFiles.length > 0 && `(${statusUpdateFiles.length} file${statusUpdateFiles.length > 1 ? 's' : ''})`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Communication Modal */}
        {showCommunicationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold mb-4">Send Message to Client</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={communication.subject}
                    onChange={(e) => setCommunication(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    value={communication.message}
                    onChange={(e) => setCommunication(prev => ({ ...prev, message: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    rows={5}
                    placeholder="Enter your message to the client..."
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCommunicationModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={sendCommunication}
                  disabled={!communication.message.trim()}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                >
                  Send Message
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Support Ticket Modal */}
        {showCreateTicketModal && selectedRequestForTicket && (
          <CreateTicketModal
            request={{
              id: selectedRequestForTicket.id,
              request_number: selectedRequestForTicket.request_number || `#${selectedRequestForTicket.id}`,
              company_name: selectedRequestForTicket.company_name,
              user: {
                name: selectedRequestForTicket.user.name,
                email: selectedRequestForTicket.user.email,
              },
            }}
            isOpen={showCreateTicketModal}
            onClose={() => {
              setShowCreateTicketModal(false);
              setSelectedRequestForTicket(null);
            }}
            onSuccess={handleTicketCreated}
          />
        )}

        {/* Document Action Modal */}
        {showDocumentActionModal && selectedDocument && (
          <DocumentActionModal
            document={selectedDocument}
            action={documentAction}
            isOpen={showDocumentActionModal}
            onClose={() => {
              setShowDocumentActionModal(false);
              setSelectedDocument(null);
            }}
            onSuccess={handleDocumentActionSuccess}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default RequestManagement;